import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { CloseIcon } from './Styles/Icons';
import {
	Dialog,
	DialogActions,
	DialogTitle,
	DialogContent,
	ButtonGroup,
	OutlinedBlueButton,
	ButtonWithLoader,
	CircularButton,
	FlexRow,
	TableContainer,
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHeaderRow,
	TableColumn,
	TableHeaderColumn,
	TablePagination,
	NotFound,
} from './Styles/SystemErrorsDialogBox';

import { clearAllRejectedInventoryItems, fetchRejectedInventoryItems, fetchAllRejectedInventoryItems } from '../../actions/inventoryActionsV2';

const escapeCsvField = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

// A rejection is either the AI reviewer's (ai_review_*) or a human reviewer's own reject
// (manual_review_*, a separate key - see internal-api's bulkActionByIds) - never both.
const isManualRejection = (item) => item.metadata?.manual_review_status === 'REJECTED';

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '');

const downloadRejectedItemsSummary = (rejectedItems) => {
	const header = ['supplier_product_id', 'source', 'reason', 'completed_at', 'rejected_by'];
	const rows = rejectedItems.map((item) =>
		isManualRejection(item)
			? [item.supplier_product_id, 'Reviewer', item.metadata?.manual_review_reason || '', '', item.metadata?.rejected_by || '']
			: [item.supplier_product_id, 'System', item.metadata?.ai_review_error || '', item.metadata?.ai_review_completed_at || '', '']
	);
	const csvContent = [header, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\n');

	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', `system-errors-${Date.now()}.csv`);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

// Toolbar-level counterpart to ViewRejectionDialogBox's single-item version - shown when the
// supplier has one or more items rejected during review, either by the AI reviewer or by a human
// reviewer's own reject. Fetches its own paginated page of items from sellerportal rather than
// taking a full list as a prop - a supplier can accumulate a lot of rejections, and the badge count
// in InventoryV2/index.jsx only ever fetches a count, not every item, to stay cheap.
const SystemErrorsDialogBox = ({ open, onTrue, onFalse, clearAllRejectedInventoryItems, fetchRejectedInventoryItems, fetchAllRejectedInventoryItems }) => {
	const [clearing, setClearing] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [items, setItems] = useState([]);
	const [totalCount, setTotalCount] = useState(0);

	const loadPage = async (page, size) => {
		setLoading(true);
		const result = (await fetchRejectedInventoryItems(page + 1, size)) || { items: [], totalCount: 0 };
		setItems(result.items || []);
		setTotalCount(result.totalCount || 0);
		setLoading(false);
	};

	useEffect(() => {
		if (!open) {
			return;
		}
		setClearing(false);
		setPageNumber(0);
		loadPage(0, pageSize);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const onPageChange = (page) => {
		setPageNumber(page);
		loadPage(page, pageSize);
	};

	const onPageSizeChange = (size) => {
		setPageSize(size);
		setPageNumber(0);
		loadPage(0, size);
	};

	const onClearHandler = async () => {
		setClearing(true);
		await clearAllRejectedInventoryItems();
		setClearing(false);
		await onTrue();
	};

	const onDownloadHandler = async () => {
		setDownloading(true);
		const allItems = (await fetchAllRejectedInventoryItems()) || [];
		downloadRejectedItemsSummary(allItems);
		setDownloading(false);
	};

	return (
		<Dialog maxWidth='md' fullWidth open={open} onClose={() => (clearing ? null : onFalse())}>
			<DialogTitle>
				Rejected Items
				<ButtonGroup alignRight>
					<CircularButton error outline disabled={clearing} onClick={() => onFalse()}>
						<CloseIcon />
					</CircularButton>
				</ButtonGroup>
			</DialogTitle>

			<DialogContent style={{ marginTop: '-30px' }}>
				<FlexRow style={{ padding: '0 1rem' }}>
					{totalCount} item{totalCount === 1 ? '' : 's'} {totalCount === 1 ? 'was' : 'were'} rejected during review and{' '}
					{totalCount === 1 ? 'is' : 'are'} waiting to be cleared. Download a summary of the reasons, or clear all of them at once to
					remove them from your pending queue - clearing does not re-apply the change, you'll need to resubmit it if you still want it.
				</FlexRow>

				<TableContainer style={{ marginTop: '1rem' }}>
					<Table>
						<TableHeader>
							<TableHeaderRow>
								<TableHeaderColumn>Item ID</TableHeaderColumn>
								<TableHeaderColumn>Source</TableHeaderColumn>
								<TableHeaderColumn>Reason</TableHeaderColumn>
								<TableHeaderColumn>Published At / Rejected By</TableHeaderColumn>
							</TableHeaderRow>
						</TableHeader>
						<TableBody>
							{!loading && items.length ? (
								items.map((item) => (
									<TableRow key={item.supplier_product_id}>
										<TableColumn>{item.supplier_product_id}</TableColumn>
										<TableColumn>{isManualRejection(item) ? 'Reviewer' : 'System'}</TableColumn>
										<TableColumn>
											{isManualRejection(item) ? item.metadata?.manual_review_reason : item.metadata?.ai_review_error || 'The system reviewer rejected this change.'}
										</TableColumn>
										<TableColumn>
											{isManualRejection(item) ? item.metadata?.rejected_by || '' : formatDateTime(item.metadata?.ai_review_completed_at)}
										</TableColumn>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableColumn colSpan={'100%'}>
										<NotFound lines={[loading ? 'Loading...' : "Couldn't find any items"]} />
									</TableColumn>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component='div'
					count={totalCount}
					page={pageNumber}
					onPageChange={(e, page) => onPageChange(parseInt(page, 10))}
					rowsPerPage={pageSize}
					onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
				/>
			</DialogContent>

			<DialogActions style={{ margin: '0 0.5rem' }}>
				<ButtonGroup alignRight>
					<OutlinedBlueButton outline disabled={!totalCount || downloading} onClick={() => onDownloadHandler()}>
						{downloading ? 'Downloading...' : 'Download Summary'}
					</OutlinedBlueButton>
					<ButtonWithLoader error loading={clearing} disabled={!totalCount} onClick={() => onClearHandler()}>
						Clear Error
					</ButtonWithLoader>
				</ButtonGroup>
			</DialogActions>
		</Dialog>
	);
};

const mapFunctionsToProps = {
	clearAllRejectedInventoryItems,
	fetchRejectedInventoryItems,
	fetchAllRejectedInventoryItems,
};

export default connect(null, mapFunctionsToProps)(SystemErrorsDialogBox);
